import { madridNow, weekday, slotsFor, cutoffMinutes, arrivalBeforeCutoff } from './crysalidas-consigna-schedule.js';

const instances = new WeakMap();
const selector = '[data-cx-consigna-booking]';
const fillOptions = (select, values, placeholder, previous) => {
  select.replaceChildren(new Option(placeholder, ''), ...values.map(value => new Option(value, value)));
  select.value = values.includes(previous) ? previous : '';
  select.disabled = values.length === 0;
};

export function initBooking(form) {
  if (instances.has(form)) return instances.get(form);
  const date = form.querySelector('input[type="date"]');
  const hour = form.querySelector('[data-cx-consigna-arrival-hour]');
  const minute = form.querySelector('[data-cx-consigna-arrival-minute]');
  const value = form.querySelector('[data-cx-consigna-arrival-value]');
  const status = form.querySelector('[data-cx-consigna-status]');
  const submit = form.querySelector('[data-cx-consigna-submit]');
  const radios = [...form.querySelectorAll('[data-cx-consigna-cutoff]')];
  if (!date || !hour || !minute || !value || !status || !submit || !radios.length) return;
  initDateCalendar(date);
  const text = (key, fallback) => form.closest?.('.cx-consigna')?.getAttribute('data-cx-consigna-' + key) || fallback;
  const preview = form.classList.contains('cx-consigna__booking-form--preview');
  const stockDisabled = new Map(radios.map(radio => [radio, radio.disabled]));
  // Revalidate at submission: the date/time may have elapsed while this page was open.
  form.noValidate = true;
  const sync = (showErrors = false) => {
    const now = madridNow();
    date.min = now.date;
    const slots = slotsFor(date.value, now);
    let error = '';
    if (date.value && weekday(date.value) === null) error = text('invalid-date', 'Elige una fecha válida.');
    else if (date.value && date.value < now.date) error = text('past-date', 'Elige hoy o una fecha posterior.');
    else if (date.value && weekday(date.value) === 0) error = text('closed-sunday', 'La consigna está cerrada los domingos. Elige otro día.');
    else if (date.value && !slots.length) error = text('no-slots', 'Hoy ya no quedan horas de llegada. Elige otro día.');
    date.setCustomValidity(error);
    date.setAttribute('aria-invalid', String(Boolean(error)));
    const restored = value.value.split(':');
    const previousHour = hour.value || restored[0];
    const previousMinute = minute.value || restored[1];
    fillOptions(hour, [...new Set(slots.map(slot => slot.slice(0, 2)))], date.value ? text('hour', 'Hora') : text('day-first', 'Elige el día primero'), previousHour);
    fillOptions(minute, slots.filter(slot => slot.startsWith(`${hour.value}:`)).map(slot => slot.slice(3)), text('minutes', 'Minutos'), previousMinute);
    const time = `${hour.value}:${minute.value}`;
    const validTime = slots.includes(time);
    value.value = validTime ? time : '';
    hour.setCustomValidity(date.value && !error && !validTime ? text('choose-time', 'Elige una hora y unos minutos de llegada.') : '');
    hour.setAttribute('aria-invalid', String(Boolean(date.value && !error && !validTime)));
    for (const radio of radios) {
      const cutoff = cutoffMinutes(radio.dataset.cxConsignaCutoff);
      radio.disabled = stockDisabled.get(radio) || cutoff === null || (validTime && !arrivalBeforeCutoff(time, cutoff));
      if (radio.disabled) radio.checked = false;
      radio.closest('label')?.classList.toggle('is-unavailable', radio.disabled);
    }
    const selected = radios.some(radio => radio.checked && !radio.disabled);
    if (!error && validTime && !selected) error = text('later-collection', 'Elige una hora de recogida posterior a tu llegada entre las tarifas disponibles.');
    if (!error && showErrors && !validTime) error = text('opening-hours', 'Elige una fecha y una hora de llegada dentro del horario de apertura.');
    status.textContent = error;
    const valid = Boolean(date.value && !error && validTime && selected);
    submit.disabled = preview || !valid;
    return valid;
  };
  date.addEventListener('change', () => sync());
  date.addEventListener('input', () => sync());
  hour.addEventListener('change', () => { value.value = ''; minute.value = ''; sync(); });
  minute.addEventListener('change', () => { value.value = ''; sync(); });
  radios.forEach(radio => radio.addEventListener('change', () => sync()));
  form.addEventListener('submit', event => {
    const valid = sync(true) && form.checkValidity();
    if (preview || !valid) {
      event.preventDefault();
      if (!valid) form.reportValidity();
    }
  });
  instances.set(form, sync);
  sync();
  return sync;
}
const initAll = () => document.querySelectorAll(selector).forEach(form => initBooking(form)?.());
if (typeof document !== 'undefined') {
  initAll();
  window.addEventListener('pageshow', initAll);
  document.addEventListener('shopify:section:load', initAll);
}


// Accessible calendar for the native Shopify date property. Sundays never accept selection.
export function initDateCalendar(input) {
  if (!input?.ownerDocument || input.dataset.cxCalendarReady) return;
  input.dataset.cxCalendarReady = 'true';
  const doc = input.ownerDocument;
  const english = doc.documentElement.lang.startsWith('en');
  const locale = english ? 'en-GB' : 'es-ES';
  const copy = english ? {
    choose: 'Choose arrival date', previous: 'Previous month', next: 'Next month',
    closed: 'Closed on Sundays', unavailable: 'Unavailable', close: 'Close calendar', empty: 'dd/mm/yyyy',
  } : {
    choose: 'Elige la fecha de llegada', previous: 'Mes anterior', next: 'Mes siguiente',
    closed: 'Domingos cerrado', unavailable: 'No disponible', close: 'Cerrar calendario', empty: 'dd/mm/aaaa',
  };
  const make = (tag, className, text) => {
    const node = doc.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  const button = (className, text) => {
    const node = make('button', className, text);
    node.type = 'button';
    return node;
  };
  const wrapper = make('div', 'cx-consigna__calendar');
  const trigger = button('cx-consigna__date-trigger');
  const triggerText = make('span', '');
  const icon = make('span', 'cx-consigna__date-icon', '\u25a6');
  icon.setAttribute('aria-hidden', 'true');
  trigger.append(triggerText, icon);
  const panel = make('div', 'cx-consigna__calendar-panel');
  panel.hidden = true;
  panel.id = input.id + '-calendar';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', copy.choose);
  trigger.setAttribute('aria-controls', panel.id);
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-haspopup', 'dialog');
  const label = input.labels?.[0];
  if (label) {
    label.htmlFor = input.id + '-trigger';
    trigger.id = label.htmlFor;
  }
  const header = make('div', 'cx-consigna__calendar-header');
  const previous = button('cx-consigna__calendar-nav', '\u2039');
  previous.setAttribute('aria-label', copy.previous);
  const heading = make('strong', 'cx-consigna__calendar-month');
  heading.setAttribute('aria-live', 'polite');
  const next = button('cx-consigna__calendar-nav', '\u203a');
  next.setAttribute('aria-label', copy.next);
  header.append(previous, heading, next);
  const weekdays = make('div', 'cx-consigna__calendar-weekdays');
  const weekdayNames = english ? ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'] : ['L','M','X','J','V','S','D'];
  weekdays.setAttribute('aria-hidden', 'true');
  weekdayNames.forEach(name => weekdays.append(make('span', '', name)));
  const grid = make('div', 'cx-consigna__calendar-days');
  grid.setAttribute('role', 'group');
  grid.setAttribute('aria-label', copy.choose);
  const footer = make('div', 'cx-consigna__calendar-footer');
  footer.append(make('span', 'cx-consigna__calendar-closed', copy.closed));
  const closeButton = button('cx-consigna__calendar-close', copy.close);
  footer.append(closeButton);
  panel.append(header, weekdays, grid, footer);
  input.before(wrapper);
  wrapper.append(input, trigger, panel);
  input.hidden = true;
  let month = '';
  const dateOf = value => new Date(value + 'T12:00:00Z');
  const iso = date => date.toISOString().slice(0, 10);
  const updateLabel = () => {
    const value = input.value;
    triggerText.textContent = weekday(value) === null ? copy.empty : new Intl.DateTimeFormat(locale, {dateStyle:'short', timeZone:'UTC'}).format(dateOf(value));
    trigger.setAttribute('aria-label', copy.choose + ': ' + triggerText.textContent);
  };
  const close = (restoreFocus = false) => {
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger.focus();
  };
  const render = (focusValue, focusFirst = false) => {
    const now = madridNow();
    const start = dateOf(month + '-01');
    heading.textContent = new Intl.DateTimeFormat(locale, {month:'long', year:'numeric', timeZone:'UTC'}).format(start);
    previous.disabled = month <= now.date.slice(0, 7);
    grid.replaceChildren();
    const offset = (start.getUTCDay() + 6) % 7;
    for (let i = 0; i < offset; i++) grid.append(make('span', 'cx-consigna__calendar-empty'));
    const count = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)).getUTCDate();
    const dayButtons = [];
    for (let day = 1; day <= count; day++) {
      const value = month + '-' + String(day).padStart(2, '0');
      const sunday = weekday(value) === 0;
      const available = slotsFor(value, now).length > 0;
      const node = button('cx-consigna__calendar-day', String(day));
      node.dataset.date = value;
      node.disabled = !available;
      node.classList.toggle('is-sunday', sunday);
      node.classList.toggle('is-selected', input.value === value);
      node.setAttribute('aria-pressed', String(input.value === value));
      if (value === now.date) node.setAttribute('aria-current', 'date');
      node.setAttribute('aria-label', new Intl.DateTimeFormat(locale, {dateStyle:'full', timeZone:'UTC'}).format(dateOf(value)) + (sunday ? '. ' + copy.closed : !available ? '. ' + copy.unavailable : ''));
      node.addEventListener('click', () => {
        // Recheck in case the calendar was left open across a day boundary.
        if (!slotsFor(value, madridNow()).length) { render(); return; }
        input.value = value;
        input.dispatchEvent(new Event('input', {bubbles:true}));
        input.dispatchEvent(new Event('change', {bubbles:true}));
        updateLabel();
        close(true);
      });
      dayButtons.push(node);
      grid.append(node);
    }
    if (focusValue || focusFirst) {
      const target = dayButtons.find(node => !node.disabled && node.dataset.date === focusValue) || dayButtons.find(node => !node.disabled);
      target?.focus();
    }
  };
  const moveMonth = delta => {
    const date = dateOf(month + '-01');
    date.setUTCMonth(date.getUTCMonth() + delta);
    month = iso(date).slice(0, 7);
    render(undefined, true);
  };
  trigger.addEventListener('click', () => {
    if (!panel.hidden) { close(); return; }
    const today = madridNow().date;
    month = (input.value >= today ? input.value : today).slice(0, 7);
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    render(input.value || today, true);
  });
  previous.addEventListener('click', () => moveMonth(-1));
  next.addEventListener('click', () => moveMonth(1));
  closeButton.addEventListener('click', () => close(true));
  wrapper.addEventListener('keydown', event => {
    if (event.key === 'Escape' && !panel.hidden) { event.preventDefault(); close(true); return; }
    const value = event.target.dataset.date;
    const delta = {ArrowLeft:-1, ArrowRight:1, ArrowUp:-7, ArrowDown:7}[event.key];
    if (!value || !delta) return;
    event.preventDefault();
    const target = dateOf(value);
    target.setUTCDate(target.getUTCDate() + delta);
    // Skip closed days without making disabled buttons keyboard-selectable.
    for (let attempts = 0; attempts < 7 && !slotsFor(iso(target), madridNow()).length; attempts++) target.setUTCDate(target.getUTCDate() + Math.sign(delta));
    const destination = iso(target);
    if (!slotsFor(destination, madridNow()).length) return;
    month = destination.slice(0, 7);
    render(destination);
  });
  doc.addEventListener('click', event => { if (!wrapper.contains(event.target)) close(); });
  wrapper.addEventListener('focusout', event => { if (event.relatedTarget && !wrapper.contains(event.relatedTarget)) close(); });
  input.addEventListener('change', updateLabel);
  input.form?.addEventListener('reset', () => { close(); queueMicrotask(updateLabel); });
  updateLabel();
}
